import { useState, useEffect, useCallback } from 'react';
import { meetingNotesApi, companiesApi } from '../lib/api';

export default function MeetingNotes({ onNavigate }) {
  const [notes, setNotes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  // AI insights + quota state
  const [insights, setInsights] = useState([]);
  const [quota, setQuota] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // New note form
  const [showForm, setShowForm] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newCompanyId, setNewCompanyId] = useState('');
  const [newDate, setNewDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [addingCompany, setAddingCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [creatingCompany, setCreatingCompany] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [notesRes, companiesRes] = await Promise.all([
        meetingNotesApi.list(),
        companiesApi.list().catch(() => ({ companies: [] })),
      ]);
      setNotes(notesRes.notes || []);
      setCompanies(companiesRes.companies || []);
    } catch (err) {
      console.error('Load meeting notes error:', err);
    }
    setLoading(false);
  }, []);

  const loadInsightsAndStatus = useCallback(async () => {
    try {
      const [insightsRes, statusRes] = await Promise.all([
        meetingNotesApi.getInsights().catch(() => ({ insights: [] })),
        meetingNotesApi.getStatus().catch(() => null),
      ]);
      setInsights(insightsRes.insights || []);
      if (statusRes) setQuota(statusRes);
    } catch (err) {
      console.error('Load insights/status error:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadInsightsAndStatus();
  }, [loadData, loadInsightsAndStatus]);

  // Auto mark insights as read when they appear
  useEffect(() => {
    const unread = insights.filter(i => !i.is_read);
    if (unread.length === 0) return;
    unread.forEach(i => {
      meetingNotesApi.markRead(i.id).catch(() => {});
    });
    setInsights(prev => prev.map(i => ({ ...i, is_read: true })));
  }, [insights]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newNote.trim() || !newCompanyId) return;
    setSaving(true);
    try {
      await meetingNotesApi.create({
        company_id: newCompanyId,
        note: newNote.trim(),
        meeting_date: newDate || null,
      });
      setNewNote('');
      setNewCompanyId('');
      setNewDate('');
      setShowForm(false);
      loadData();
    } catch (err) {
      alert('Failed to save note: ' + err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await meetingNotesApi.delete(id);
      setNotes(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleRunAnalysis = async () => {
    setAnalyzing(true);
    try {
      await meetingNotesApi.runAnalysis();
      const [insightsRes, statusRes] = await Promise.all([
        meetingNotesApi.getInsights().catch(() => ({ insights: [] })),
        meetingNotesApi.getStatus().catch(() => null),
      ]);
      setInsights(insightsRes.insights || []);
      if (statusRes) setQuota(statusRes);
    } catch (err) {
      alert('Analysis failed: ' + err.message);
    }
    setAnalyzing(false);
  };

  // Group notes by company (for select filter)
  const companyMap = {};
  notes.forEach(n => {
    const cName = n.companies?.name || 'Unknown Company';
    if (!companyMap[cName]) companyMap[cName] = [];
    companyMap[cName].push(n);
  });
  const companyNames = Object.keys(companyMap).sort();

  // Enhanced filter + search (matches note text OR insight summary/signal text)
  const filteredNotes = notes.filter(n => {
    if (filter !== 'all' && n.company_id !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const noteMatch = n.note.toLowerCase().includes(q);
      const insight = insights.find(i => i.note_id === n.id);
      const summaryMatch = insight?.summary?.toLowerCase().includes(q);
      const signalMatch = insight?.overlaps?.some(o => o.signal?.toLowerCase().includes(q));
      if (!noteMatch && !summaryMatch && !signalMatch) return false;
    }
    return true;
  });

  // Group filtered notes by company for display
  const filteredByCompany = {};
  filteredNotes.forEach(n => {
    const cName = n.companies?.name || 'Unknown Company';
    if (!filteredByCompany[cName]) filteredByCompany[cName] = [];
    filteredByCompany[cName].push(n);
  });
  const filteredCompanyNames = Object.keys(filteredByCompany).sort();

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const formatTime = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  const confidenceDot = (confidence) => {
    const colors = { high: '#10B981', medium: '#F59E0B', low: '#9CA3AF' };
    return (
      <span
        className="w-1.5 h-1.5 rounded-full inline-block"
        style={{ backgroundColor: colors[confidence] || '#9CA3AF' }}
      />
    );
  };

  const timingColor = (score) => {
    if (score > 70) return 'text-emerald-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-gray-400';
  };

  const parseSummaryBullets = (summary) => {
    if (!summary) return [];
    return summary.split('\n').map(s => s.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);
  };

  const InsightCard = ({ insight, note }) => {
    const hasPitchWindow = insight.overlaps?.some(o => o.pitch_window === true);
    const bullets = parseSummaryBullets(insight.summary);

    return (
      <div className="mt-3 pt-3 border-t border-vault-border">
        {/* Pitch Window Banner */}
        {hasPitchWindow && (
          <div className="mb-2 px-3 py-2 rounded-lg border-2 border-amber-400/50 bg-amber-50/50">
            <p className="font-semibold text-amber-700 text-xs">Pitch Window Open</p>
            {insight.overlaps?.filter(o => o.pitch_window).map((o, i) => (
              <p key={i} className="text-[11px] text-amber-600 mt-0.5">
                {note.companies?.name} &middot; {o.idea_title} &middot; {o.category}
              </p>
            ))}
          </div>
        )}

        {/* Summary bullets */}
        {bullets.length > 0 && (
          <ul className="space-y-0.5 mb-2">
            {bullets.map((b, i) => (
              <li key={i} className="text-xs text-gray-500 flex items-start gap-1.5">
                <span className="text-gray-300 shrink-0 mt-0.5">&bull;</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Idea overlap chips */}
        {insight.overlaps?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {insight.overlaps.map((o, i) => (
              <button
                key={i}
                onClick={() => onNavigate?.('vault')}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 cursor-pointer hover:bg-indigo-100"
              >
                {confidenceDot(o.confidence)}
                <span>{o.idea_title}</span>
              </button>
            ))}
          </div>
        )}

        {/* Trend signal badges */}
        {insight.overlaps?.some(o => o.category) && (
          <div className="flex flex-wrap gap-1 mb-2">
            {[...new Set(insight.overlaps.filter(o => o.category).map(o => o.category))].map((cat, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700"
              >
                {cat}
              </span>
            ))}
          </div>
        )}

        {/* Next step */}
        {insight.next_step && (
          <p className="text-xs text-vault-gold font-medium mt-2">
            &rarr; {insight.next_step}
          </p>
        )}

        {/* Timing score */}
        {typeof insight.timing_score === 'number' && (
          <p className={`text-[10px] font-medium mt-1 ${timingColor(insight.timing_score)}`}>
            Timing score: {insight.timing_score}/100
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><p className="text-sm text-gray-400">Loading notes...</p></div>;
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl text-vault-navy">Meeting Notes</h1>
          <p className="text-sm text-gray-400 mt-1">Track interactions with companies. Notes improve your AI match scores.</p>
        </div>
        <div className="flex items-start gap-3 shrink-0">
          {/* Run Analysis button */}
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={handleRunAnalysis}
              disabled={analyzing || (quota && !quota.can_run)}
              className="px-5 py-2.5 rounded-btn text-sm font-semibold text-white disabled:opacity-50 flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              {analyzing && (
                <span className="w-2 h-2 rounded-full bg-white/70 animate-pulse" />
              )}
              {analyzing ? 'Analyzing...' : 'Run Analysis'}
            </button>
            {quota && (
              <span className="text-xs text-gray-400">
                {quota.can_run
                  ? `${quota.used} of ${quota.limit} runs used this month`
                  : `Resets ${quota.reset_date}`}
              </span>
            )}
          </div>

          {/* Add Note button */}
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-5 py-2.5 rounded-btn text-sm font-semibold text-white shrink-0"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            {showForm ? 'Cancel' : '+ Add Note'}
          </button>
        </div>
      </div>

      {/* Add note form */}
      {showForm && (
        <div className="glass p-6 mb-6">
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Company *</label>
                {addingCompany ? (
                  <div className="flex gap-2">
                    <input
                      value={newCompanyName}
                      onChange={e => setNewCompanyName(e.target.value)}
                      placeholder="Company name..."
                      className="flex-1 px-3 py-2.5 border border-vault-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vault-gold"
                      autoFocus
                    />
                    <button
                      type="button"
                      disabled={creatingCompany || !newCompanyName.trim()}
                      onClick={async () => {
                        setCreatingCompany(true);
                        try {
                          const res = await companiesApi.create(newCompanyName.trim());
                          const company = res.company;
                          if (!companies.find(c => c.id === company.id)) {
                            setCompanies(prev => [...prev, company].sort((a, b) => a.name.localeCompare(b.name)));
                          }
                          setNewCompanyId(company.id);
                          setAddingCompany(false);
                          setNewCompanyName('');
                        } catch (err) {
                          alert('Failed to add company: ' + err.message);
                        }
                        setCreatingCompany(false);
                      }}
                      className="px-3 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
                    >
                      {creatingCompany ? '...' : 'Add'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAddingCompany(false); setNewCompanyName(''); }}
                      className="px-3 py-2.5 text-sm text-gray-400 hover:text-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select
                      value={newCompanyId}
                      onChange={e => {
                        if (e.target.value === '__add_new__') {
                          setAddingCompany(true);
                        } else {
                          setNewCompanyId(e.target.value);
                        }
                      }}
                      className="flex-1 px-3 py-2.5 border border-vault-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vault-gold"
                    >
                      <option value="">Select company...</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                      <option value="__add_new__">+ Add new company</option>
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Meeting Date</label>
                <input
                  type="date"
                  value={newDate}
                  onChange={e => setNewDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-vault-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vault-gold"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Note *</label>
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="What did you discuss? Any feedback on specific ideas? Key takeaways?"
                rows={4}
                className="w-full px-3 py-2.5 border border-vault-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vault-gold resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={saving || !newNote.trim() || !newCompanyId}
              className="px-5 py-2.5 rounded-btn text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              {saving ? 'Saving...' : 'Save Note'}
            </button>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        <input
          type="text"
          placeholder="Search notes and insights..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 max-w-xs px-3 py-2 border border-vault-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vault-gold"
        />
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="px-3 py-2 border border-vault-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vault-gold"
        >
          <option value="all">All Companies</option>
          {companyNames.map(name => {
            const companyId = companyMap[name][0]?.company_id;
            return <option key={companyId} value={companyId}>{name}</option>;
          })}
        </select>
        <span className="text-xs text-gray-400">{filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Notes list grouped by company */}
      {filteredNotes.length === 0 ? (
        <div className="glass p-10 text-center">
          <div className="text-3xl mb-3">📝</div>
          <h2 className="font-heading text-xl text-vault-navy mb-2">
            {notes.length === 0 ? 'No meeting notes yet' : 'No notes match your filter'}
          </h2>
          <p className="text-sm text-gray-400">
            {notes.length === 0
              ? 'Add notes from company meetings to improve your AI match scores and track interactions.'
              : 'Try a different search term or company filter.'}
          </p>
        </div>
      ) : (
        <div>
          {filteredCompanyNames.map(companyName => (
            <div key={companyName}>
              <h3 className="font-heading text-lg text-vault-navy mt-6 mb-3 first:mt-0">{companyName}</h3>
              <div className="space-y-3">
                {filteredByCompany[companyName].map(note => {
                  const insight = insights.find(i => i.note_id === note.id);
                  return (
                    <div key={note.id} className="glass p-5 group hover:shadow-glass-hover transition-all">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {note.companies?.logo_url && (
                              <img src={note.companies.logo_url} alt="" className="w-5 h-5 rounded object-contain" />
                            )}
                            <span className="text-sm font-semibold text-vault-navy">{note.companies?.name || 'Unknown'}</span>
                            {note.meeting_date && (
                              <span className="text-xs text-vault-gold font-medium">{formatDate(note.meeting_date)}</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{note.note}</p>
                          <p className="text-xs text-gray-300 mt-2">{formatTime(note.created_at)}</p>

                          {/* Inline insight card */}
                          {insight && <InsightCard insight={insight} note={note} />}
                        </div>
                        <button
                          onClick={() => handleDelete(note.id)}
                          className="text-gray-300 hover:text-vault-error opacity-0 group-hover:opacity-100 transition-opacity ml-4 shrink-0 text-lg"
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
